"use client";

import { useMemo } from "react";

import LiveTimingCountdown from "@/components/engine-visuals/LiveTimingCountdown";
import type { ChoghadiyaPeriodLite, ChoghadiyaResponse } from "@/lib/api/panchang";

interface ChoghadiyaSummaryProps {
  result: ChoghadiyaResponse;
}

/**
 * The period containing this moment, chosen by comparing absolute instants.
 *
 * `start_time`/`end_time` are wall-clock strings in the response's own tz_offset
 * — comparing them against a browser clock (or against IST) misidentifies the
 * period for every city outside that zone and cannot represent a night window
 * that crosses midnight. Only `starts_at`/`ends_at` are moments.
 */
function activePeriod(result: ChoghadiyaResponse): ChoghadiyaPeriodLite | undefined {
  const now = Date.now();
  const periods = [...(result.day_choghadiya ?? []), ...(result.night_choghadiya ?? [])];

  return periods.find((period) => {
    if (!period.starts_at || !period.ends_at) return false;
    const startsAt = new Date(period.starts_at).getTime();
    const endsAt = new Date(period.ends_at).getTime();
    if (!Number.isFinite(startsAt) || !Number.isFinite(endsAt)) return false;
    return now >= startsAt && now < endsAt;
  });
}

export function ChoghadiyaSummary({ result }: ChoghadiyaSummaryProps) {
  const current = useMemo(() => activePeriod(result), [result]);

  return (
    <div className="card-sacred p-6 sm:p-8">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
        <div>
          <span className="block text-eyebrow font-semibold uppercase tracking-eyebrow mb-1 text-text-muted">
            Sunrise
          </span>
          <span className="text-subtitle-small font-bold tabular-nums text-text-primary">{result.sunrise}</span>
        </div>
        <div>
          <span className="block text-eyebrow font-semibold uppercase tracking-eyebrow mb-1 text-text-muted">
            Sunset
          </span>
          <span className="text-subtitle-small font-bold tabular-nums text-text-primary">{result.sunset}</span>
        </div>
        <div>
          <span className="block text-eyebrow font-semibold uppercase tracking-eyebrow mb-1 text-text-muted">
            Day
          </span>
          <span className="text-subtitle-small font-bold text-text-primary">{result.vara.name}</span>
        </div>
        <div>
          <span className="block text-eyebrow font-semibold uppercase tracking-eyebrow mb-1 text-text-muted">
            Vaar
          </span>
          <span className="text-subtitle-small font-bold text-text-primary">{result.vara.name_hi}</span>
        </div>
      </div>

      {current?.ends_at && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3 border-t border-border-subtle/80 pt-4">
          <span className="text-xs font-medium text-text-secondary">
            Active Choghadiya: <strong>{current.name}</strong>
            {current.name_hi ? ` (${current.name_hi})` : null}
          </span>
          <LiveTimingCountdown
            labelEn={current.name}
            labelHi={current.name_hi ?? current.name}
            endsAt={current.ends_at}
            isAuspicious={current.quality === "best" || current.quality === "good"}
          />
        </div>
      )}
    </div>
  );
}
